// Angular
import { Injectable } from '@angular/core';

// Models
import { BannersHelper, Banners } from '../../models/b2b/banners';
import { SearchType } from '../../models/b2b/searchType';
import { User } from '../../models/b2b/user';
import { BannerHeader } from '../../models/b2b/segmentation';
import { BannerHelper } from '../../models/b2b/banner';

// Rxjs
import { Observable, of } from 'rxjs';

// Services
import { ApiService } from '../api/api.service';
import { map } from 'rxjs/operators';
import { PromoPortada, PromoPortadaHelper } from 'app/shared/models/b2b/promoPortada';
import { validateApiResponse } from 'app/shared/lib/custom-rxjs-operators/api-response.operator';

@Injectable()
export class SoledadBannerService {
    constructor(private api: ApiService) {}

    getBanners(selectedAddress: number, searchType: SearchType): Observable<Banners> {
        const banners = BannersHelper.create();

        const vehicleType = new Map<string, string>([
            [SearchType.all, ''],
            [SearchType.car, 'TURISMO'],
            [SearchType.wagon, 'CAMIONETA'],
            [SearchType.moto, 'MOTO'],
            [SearchType.allroad, '4X4'],
            [SearchType.truck, 'CAMION'],
            [SearchType.tractor, 'AGRICOLA'],
            [SearchType.industrial, 'INDUSTRIAL'],
            [SearchType.other, 'OTROSPRODUCTOS'],
            // [SearchType.parts, 'PARTS'],
            [SearchType.oportunities, 'OPORTUNIDADES'],
        ]);

        if (!selectedAddress && typeof selectedAddress !== 'number') {
            return of(banners);
        }

        // Params
        const params = {};
        params['TIPO_VEHICULO'] = '';
        if (searchType !== undefined) {
            params['TIPO_VEHICULO'] =
                searchType === SearchType.all ? vehicleType.get(SearchType.all) : vehicleType.get(searchType);
        }
        params['DIRECCIONENVIO'] = selectedAddress;

        // Call
        return this.api.callSoledad('b2bNew.dbo.HTML_PortadaLeer', params).pipe(
            map((data) => {
                data.value.map((row) => {
                    const banner = BannerHelper.transformFromApi(row, this.api.environment);
                    switch (row['PO_ZONAPANTALLA']) {
                        case 'SUPERIOR':
                            banners.superior.push(banner);
                            break;
                        case 'CENTRO':
                            banners.centro.push(banner);
                            break;
                        case 'DERECHA':
                            banners.derecha.push(banner);
                            break;
                        case 'INTERSTITIAL':
                            banners.interstitial.push(banner);
                            break;
                    }
                });

                return banners;
            }),
        );
    }

    // Load banner from localStorage
    loadBanner(user: User): any[] {
        const banners = JSON.parse(sessionStorage.getItem(user.clienteCodigo + 'bannerInterstitial'));
        return Array.isArray(banners) ? banners : [];
    }

    // Save banner on localStorage
    saveBanner(user: User) {
        sessionStorage.setItem(user.clienteCodigo + 'bannerInterstitial', '1');
    }

    saveBannerId(user: User, id: string) {
        let banners = JSON.parse(sessionStorage.getItem(user.clienteCodigo + 'bannerInterstitial'));
        let toSave = [];

        if (banners && Array.isArray(banners)) {
            banners.push(id);
            toSave = banners;
        } else {
            toSave.push(id);
        }

        sessionStorage.setItem(user.clienteCodigo + 'bannerInterstitial', JSON.stringify(toSave));
    }

    // Banners Admin Methods
    // =====================
    getBannersHeaders(): Observable<BannerHeader[]> {
        const params = {};
        return this.api.callSoledad('b2bNew.dbo.HTML_PantallaCabeceraBuscar', params).pipe(
            map((data) => {
                // console.log('getBannersHeaders', data);
                return data.value;
            }),
        );
    }

    deleteBannersHeaders(item: any) {
        const params = {};
        params['PANTALLA'] = item.PANTALLA;
        params['SEGMENTACION'] = item.PANTALLA_SEGMENTACION;
        return this.api.callSoledad('b2bNew.dbo.HTML_PantallaCabeceraBorrar', params).pipe(
            map((data) => {
                // console.log('deleteBannersHeaders', data);
                return data;
            }),
        );
    }

    getBannerScreen(id: number) {
        const params = {};
        params['PANTALLA_SEGMENTACION'] = id;

        return this.api.callSoledad('b2bNew.dbo.HTML_PantallaDetalleLeer', params).pipe(
            map((data) => {
                // console.log('getBannerScreen', data);
                return data.value;
            }),
        );
    }

    getSegmentation(): Observable<any> {
        const params = {};
        return this.api.callSoledad('b2bNew.dbo.HTML_SegmentacionLeer', params).pipe(
            map((data) => {
                // console.log('getSegmentation', data);
                return data.value;
            }),
        );
    }

    saveBannerItem(item: any) {
        // console.log('saveBannerItem', item);

        const params = {};
        params['PANTALLA_SEGMENTACION'] = item.PANTALLA_SEGMENTACION;
        params['PANTALLA_SEGMENTACION_MODIFICAR'] = item.PANTALLA_SEGMENTACION;
        params['FILA'] = item.FILA;

        //TODO: pdte cambio en el servicio para que acepte el -1
        if (!item.NEW) {
            params['FILA_MODIFICAR'] = item.FILA_ORIGEN >= 0 ? item.FILA_ORIGEN : item.FILA;
        } else {
            params['FILA_MODIFICAR'] = -1;
        }

        params['POSICION'] = item.POSICION;
        params['POSICION_MODIFICAR'] = item.POSICION;
        params['OBJETO'] = item.OBJETO_ID;
        params['VISUALIZACION'] = item.VISUALIZACION_ID;
        params['IMAGEN'] = item.IMAGEN;
        params['ENLACE'] = item.ENLACE;
        params['ACCION'] = item.ACCION;

        return this.api.callSoledad('b2bNew.dbo.HTML_PantallaDetalleGrabar', params).pipe(
            map((data) => {
                // console.log('HTML_PantallaDetalleGrabar', data);
                return data.value;
            }),
        );
    }

    deleteBannerItem(item: any) {
        // console.log('deleteBannerItem', item);
        const params = {};
        params['PANTALLA_SEGMENTACION'] = item.PANTALLA_SEGMENTACION;
        params['FILA'] = item.FILA;
        params['POSICION'] = item.POSICION;

        return this.api.callSoledad('b2bNew.dbo.HTML_PantallaDetalleBorrar', params).pipe(
            map((data) => {
                // console.log('deleteBannerItem', data);
                return data.value;
            }),
        );
    }

    saveBannerScreen(item: any) {
        // console.log('saveBannerScreen' , item);
        const params = {};
        params['PANTALLA'] = item.PANTALLA;
        params['SEGMENTACION'] = item.SEGMENTACION;
        params['DESCRIPCION'] = item.DESCRIPCION;
        // params['ACCESO'] = item.ACCESO;

        return this.api.callSoledad('b2bNew.dbo.HTML_PantallaCabeceraGrabar', params).pipe(
            map((data) => {
                // console.log('saveBannerScreen' , data);
                return data;
            }),
        );
    }

    getPromosPortada(clienteCodigo: string): Observable<PromoPortada[]> {
        const params = [];
        params['CLIENTE'] = clienteCodigo;
        return this.api.callSoledad('b2bNew.dbo.HTML_PromosPortada', params).pipe(
            validateApiResponse('Error obteniendo promociones de portada' + '. Params: ' + JSON.stringify(params)),
            map((promos: any[]) => {
                return promos
                    .map((promo: any) => PromoPortadaHelper.fromApi(promo))
                    .filter((i: PromoPortada) => i.urlImagen || i.urlBanner);
            }),
        );
    }
}
